<template>
  <div>
    <span class="caption font-weight-medium" v-if="label">
      {{ label }} <span class="error--text" v-if="required">*</span>
    </span>
    <v-menu
      ref="menu"
      class="rounded-lg"
      v-model="menu"
      style="overflow: hidden"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="
            singleDate
              ? formattedDate(model)
              : model.map((date) => localeDate(date)).join(', ')
          "
          append-icon="event"
          readonly
          :hide-details="hideDetails"
          :rules="rules"
          :disabled="!allowEdit"
          dense
          :class="color === 'white' ? 'custom-field-white' : 'custom-field'"
          solo
          flat
          filled
          :readonly="readonly"
          v-bind="attrs"
          v-on="on"
          class="input-text-center"
        >
          <template v-slot:prepend-inner>
            <v-btn icon @click.stop="changeDay(-1)" :disabled="!allowEdit">
              <v-icon>chevron_left</v-icon>
            </v-btn>
          </template>

          <template v-slot:append>
            <v-btn icon @click.stop="changeDay(1)" :disabled="!allowEdit" class="mr-1">
              <v-icon>chevron_right</v-icon>
            </v-btn>
            <v-icon v-if="!confirm">event</v-icon>
            <v-btn v-else icon @click.prevent="allowEdit = !allowEdit">
              <v-icon>{{ allowEdit ? "mdi-lock-open" : "mdi-lock" }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </template>

      <v-date-picker
        v-model="selectedDate"
        class="rounded-lg custom-shadow"
        :range="!singleDate && !multiple"
        :multiple="multiple"
        color="primary"
        :readonly="readonly"
        :min="min"
        :max="max"
        :locale="locale"
        :allowed-dates="allowedDates"
        :picker-date="pickerDate"
        scrollable
        @input="onDateInput"
      >
        <template v-if="!singleDate">
          <v-spacer></v-spacer>
          <v-btn text @click="menu = false">Cancel</v-btn>
          <v-btn color="success" class="rounded-lg" @click="saveSelection">Save</v-btn>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel";

export default {
  mixins: [hasModel],
  props: {
    label: {
      type: String,
      required: false,
      default: "",
    },
    disallowedDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: true,
    },
    singleDate: {
      type: Boolean,
      required: false,
      default: true,
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    confirm: {
      type: Boolean,
      required: false,
      default: false,
    },
    color: {
      type: String,
      required: false,
      default: "default",
    },
    min: {
      type: [String, Boolean],
      required: false,
      default: undefined,
    },
    max: {
      type: [String, Boolean],
      required: false,
      default: undefined,
    },
    pickerDate: {
      type: String,
      required: false,
      default: undefined,
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
  },
  data() {
    return {
      menu: false,
      selectedDate: this.singleDate ? null : [],
      allowEdit: true,
    };
  },
  created() {
    if (this.disabled || this.confirm) this.allowEdit = false;
  },
  watch: {
    disabled() {
      this.allowEdit = !(this.confirm || this.disabled);
    },
    menu(isOpen) {
      if (isOpen) {
        // When menu opens, copy the current committed model value to selectedDate
        if (this.singleDate) {
          this.selectedDate = this.model; // model is from hasModel, reflects prop 'value'
        } else {
          // For range, ensure it's an array and make a copy
          this.selectedDate = Array.isArray(this.model) ? [...this.model] : [];
        }
      } else {
        this.allowEdit = !(this.confirm || this.disabled);
      }
    },
    // Watch for external changes to the model (prop 'value')
    model: {
      handler(newValue) {
        // Only sync selectedDate from model if the menu is NOT open.
        // If menu is open, user might be in the middle of a selection.
        // When menu opens, the 'menu' watcher will correctly initialize selectedDate.
        if (!this.menu) {
          if (this.singleDate) {
            this.selectedDate = newValue;
          } else {
            this.selectedDate = Array.isArray(newValue) ? [...newValue] : [];
          }
        }
      },
      immediate: true, // Run handler immediately on component creation to set initial selectedDate
      deep: true, // Necessary for array model (date range)
    },
  },
  methods: {
    changeDay(days) {
      if (this.multiple) return;

      if (this.singleDate) {
        // If no model, start from today
        const baseDate = this.model ? new Date(this.model) : new Date();
        baseDate.setDate(baseDate.getDate() + days);
        const newDate = baseDate.toISOString().substr(0, 10);

        // Check min/max constraints
        if (this.min && newDate < this.min) return;
        if (this.max && newDate > this.max) return;

        this.model = newDate;
      } else {
        // For range: if no model or incomplete, start from today
        if (!Array.isArray(this.model) || this.model.length === 0) {
          const today = new Date();
          const todayStr = today.toISOString().substr(0, 10);

          const startDate = new Date(today);
          startDate.setDate(startDate.getDate() + days);
          const newStart = startDate.toISOString().substr(0, 10);

          // Check min/max constraints
          if (this.min && newStart < this.min) return;
          if (this.max && newStart > this.max) return;

          this.model = [newStart, newStart];
        } else if (this.model.length === 2) {
          const startDate = new Date(this.model[0]);
          const endDate = new Date(this.model[1]);

          startDate.setDate(startDate.getDate() + days);
          endDate.setDate(endDate.getDate() + days);

          const newStart = startDate.toISOString().substr(0, 10);
          const newEnd = endDate.toISOString().substr(0, 10);

          // Check min/max constraints
          if (this.min && newStart < this.min) return;
          if (this.max && newEnd > this.max) return;

          this.model = [newStart, newEnd];
        }
      }
    },
    onDateInput() {
      if (this.singleDate) {
        this.saveSelection();
      }
    },
    formattedDate(date) {
      if (!date) return "";
      return this.localeDate(date);
    },
    allowedDates(val) {
      return !this.disallowedDates.includes(val);
    },
    saveSelection() {
      if (this.multiple && Array.isArray(this.selectedDate)) {
        this.model = [...new Set(this.selectedDate)].sort();
      } else if (!this.singleDate && Array.isArray(this.selectedDate)) {
        if (this.selectedDate.length === 2) {
          // Ensure dates are sorted (YYYY-MM-DD format allows string sort)
          const sorted = [...this.selectedDate].sort();
          this.model = sorted; // Assign a new array for reactivity
        } else if (this.selectedDate.length === 1) {
          // If only one date is selected, set model to [date, date]
          const date = this.selectedDate[0];
          this.model = [date, date];
        }
      } else if (this.singleDate && this.selectedDate) {
        const newDate =
          typeof this.selectedDate === "string"
            ? "" + this.selectedDate // Force new string instance
            : this.selectedDate;
        this.model = newDate;
      }
      this.menu = false; // Close the menu
    },
  },
  computed: {
    locale() {
      return "en-ZA";
    },
  },
};
</script>

<style>
.input-text-center input {
  text-align: center;
}
</style>
